"use client";

import { type FC, useState } from "react";
import AnimatedCard from "./AnimatedCard";
import BigButton from "@/components/shared/card/BigButton";
import CurrencyForm from "@/pages/Sniper/components/CurrencyForm";
import InfoBox from "./InfoBox";
import SnipeDetailsGrid from "./SnipeDetailsGrid";
import WalletBox from "./wallet-box";
import { shortenAddress } from "@utils/address";
// Define the props interface
import type { IAsset } from "@/api/IWalletController";
import IconButton from "@components/shared/card/IconButton";
import IconButtonsList from "@components/shared/card/IconsList";

export interface SnipeProps {
    className?: string;
    tokenName: string | null;
    tokenAddress: string;
    initialAmount: string | number;
    profitPercentage: string | number;
    profitClassName?: string;
    price: number | string;
    priceTrend?: "up" | "down" | "none";
    marketCap: number | string;
    totalSupply?: string;
    priceInTon?: string;
    mempadLoading?: boolean;
    mempadError?: string | null;
    age: number | string;
    sendAmount: string | number;
    setSendAmount: (value: string) => void;
    balance: string | number;
    usdPrice: string | number;
    onSellClick: () => Promise<void> | void;
    isSellDisabled: boolean;
    status: "Active" | "Cancelled" | "In Progress" | "Success" | "Failed" | "Rejected" | "Migrated/Unknown";
    watchingWallet?: string;
    assetOutInfo?: IAsset;
    tokenSymbol?: string;
    tokenImage?: string;
    walletAddress?: string;
    assetInInfo?: IAsset;
    revealed?: boolean;
    updateSnipe?: () => Promise<void> | void;
    collapseSnipe?: () => Promise<void> | void;
    guid?: string;
    hide?: boolean;
    memePad?: boolean;
    collapsed?: boolean;
    onDeleteClick?: () => void;
    memePadType?: number;
}

export const Snipe: FC<SnipeProps> = ({
    className,
    tokenName,
    tokenAddress,
    initialAmount,
    profitPercentage,
    profitClassName = "", // unused, safe to ignore warning
    price,
    priceTrend = "none",
    marketCap,
    totalSupply,
    priceInTon,
    mempadLoading,
    mempadError,
    age,
    sendAmount,
    setSendAmount,
    balance,
    usdPrice,
    onSellClick,
    status,
    isSellDisabled,
    watchingWallet,
    assetOutInfo,
    tokenSymbol, // unused, safe to ignore warning
    tokenImage,
    walletAddress,
    assetInInfo,
    revealed = true,
    updateSnipe,
    guid,
    hide,
    memePad,
    collapsed,
    onDeleteClick,
    memePadType, // unused, safe to ignore warning
}) => {
    const walletAddressShort = shortenAddress(walletAddress || "");
    const watchingWalletShort = shortenAddress(watchingWallet || "");
    const tokenAddressShort = shortenAddress(tokenAddress || "");
    const [isCollapsed, setIsCollapsed] = useState<boolean>(!!collapsed);

    // Check if the delete button should be shown
    const showDeleteButton = ["Failed", "Cancelled", "Rejected"].includes(status) && onDeleteClick;

    return (
        <AnimatedCard
            revealed={revealed}
            hide={hide}
            collapsed={isCollapsed}
            status={status}
            guid={guid}
            tokenAddress={tokenAddress}
            className={className}
        >
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <span className="font-bold">{tokenName || ""}</span>
                    <span className="text-gray-300 text-sm">{walletAddressShort}</span>
                </div>
                <IconButtonsList>
                    {status !== "Failed" && status !== "Cancelled" ? (
                        <IconButton type="Reload" onClick={updateSnipe} />
                    ) : (
                        <>
                            <IconButton
                                type="ChevronDown"
                                onClick={() => setIsCollapsed((c: boolean) => !c)}
                                className={isCollapsed ? "" : "-rotate-180"}
                            />
                            {showDeleteButton && (
                                <IconButton
                                    type="Trash"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                />
                            )}
                        </>
                    )}
                </IconButtonsList>
            </div>

            {/* MEMEPAD TOKEN INFO */}

            {/* Info Boxes - Use props */}
            <div className="flex flex-col gap-3">
                <WalletBox
                    label={watchingWallet ? "Watching wallet" : "Token address"}
                    value={watchingWallet ? watchingWalletShort : tokenAddressShort}
                />
                <div className="flex gap-3">
                    <InfoBox label="Sent:" value={initialAmount} image={assetInInfo?.image} />
                    {assetOutInfo && <InfoBox label="Asset:" value={assetOutInfo.name} image={tokenImage} />}
                    {status === "Success" && <InfoBox label="Profit:" value={`${profitPercentage}%`} />}
                </div>
            </div>

            {status === "Success" && (
                <SnipeDetailsGrid price={priceInTon ?? "N/A"} priceTrend={priceTrend} marketCap={marketCap} age={age} />
            )}

            {status === "Success" && (
                <CurrencyForm
                    title="Send"
                    amount={sendAmount}
                    setAmount={setSendAmount}
                    balance={balance}
                    price={typeof usdPrice === "number" ? usdPrice : Number(usdPrice) || 0}
                />
            )}

            {/* Button */}

            {status === "Active" && <BigButton text="Cancel" onClick={onSellClick} variant="faded" />}
            {status === "Success" && (
                <BigButton
                    text={isSellDisabled ? "Set amount" : "Sell"}
                    onClick={async () => {
                        if (memePad) {
                            try {
                                const { useMemeSwap } = await import("@/stores/meme-swap.store");
                                if (assetInInfo) useMemeSwap.getState().setAssetIn(assetInInfo);
                                useMemeSwap.getState().setAssetInAmount(String(sendAmount));
                                const { initialAssets } = await import("@/stores/assets.store");
                                const tonAsset = initialAssets.find((a) => a.symbol === "TON");
                                if (tonAsset && tonAsset.guid) {
                                    useMemeSwap.getState().setMemeSwapTargetAddress(tonAsset.guid);
                                } else {
                                    console.warn("TON asset not found for meme swap target address");
                                }
                                const result = await useMemeSwap.getState().executeSwap();
                                if (result && result.status === "success") {
                                    console.log("Meme swap executed", result);
                                } else {
                                    console.error("Meme swap failed", result);
                                }
                            } catch (err) {
                                console.error("Meme swap failed", err);
                            }
                            return;
                        }
                        if (isSellDisabled) return;

                        try {
                            const { useSwap } = await import("@/stores/swap.store");
                            const setSellAsset = useSwap.getState().setSellAsset;
                            const setBuyAsset = useSwap.getState().setBuyAsset;
                            const setSwapAmount = useSwap.getState().setSwapAmount;
                            const executeSwap = useSwap.getState().executeSwap;
                            // Set the current Snipe's asset/amount context in the store
                            if (assetInInfo) setSellAsset(assetInInfo);
                            // Always set buy asset to TON for snipe
                            const { initialAssets } = await import("@/stores/assets.store");
                            const tonAsset = initialAssets.find((a) => a.symbol === "TON");
                            if (tonAsset) setBuyAsset(tonAsset);
                            setSwapAmount(String(sendAmount));
                            // Now execute the swap
                            console.log("Swap executed yoo", executeSwap);
                            const result = await executeSwap();
                            if (result) {
                                // Success feedback (replace with toast if available)
                                console.log("Swap executed", result);
                            }
                        } catch (err) {
                            console.error("Swap failed", err);
                        }
                    }}
                    variant="default"
                    disabled={isSellDisabled}
                />
            )}
            {status === "Failed" && <BigButton text="Failed" onClick={onSellClick} variant="error" />}
            {status === "Cancelled" && <BigButton text="Cancelled" onClick={onSellClick} variant="error" />}
        </AnimatedCard>
    );
};
